<template>
  <div class="property-description">
    <FormGroup>
      <FormField :error="form.fieldErrors('description')">
        <div class="field" :aria-busy="isGeneratingDescription">
          <Textarea class="textarea" rows="18" v-model="form.fields.description" :disabled="isGeneratingDescription" />
          <!-- Laid over the textarea rather than in its place, so the box keeps its size when the text lands -->
          <Transition name="writing">
            <PropertyDescriptionWriting v-if="isGeneratingDescription" class="writing" />
          </Transition>
        </div>
      </FormField>
    </FormGroup>

    <div class="controls">
      <IconButton
        :variant="'secondary'"
        :size="'small'"
        :icon="'copy'"
        :disabled="!form.fields.description || form.submitting || isGeneratingDescription"
        @click="copyDescription"
      />

      <Button :variant="'secondary'" :size="'small'" :disabled="isGeneratingDescription" @click="showHighlightsModal">
        <Icon name="stars-03" size="extra-small" />
        <span>Improve</span>
      </Button>

      <Button :variant="'secondary'" :size="'small'" :disabled="isGeneratingDescription" @click="generateDescription">
        <Icon name="refresh" size="extra-small" />
        {{ form.fields.description ? 'Regenerate' : 'Generate' }}
      </Button>
    </div>

    <TPropertyDetailsDescriptionHighlightsModal @highlights-updated="generateDescription" ref="highlightsModalRef" />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

import {
  Button,
  FormField,
  FormGroup,
  Icon,
  Textarea,
  IconButton,
  useForm,
  useAlertsStore,
} from '@tallo/design-system';

import { usePropertyStore } from '../../../stores/property.store';
import TPropertyDetailsDescriptionHighlightsModal from './property-description-highlights-modal.vue';
import PropertyDescriptionWriting from './property-description-writing.vue';

const highlightsModalRef = ref();
const alertsStore = useAlertsStore();
const propertyStore = usePropertyStore();
const isGeneratingDescription = ref(false);
const form = useForm<{ description: string }>({
  description: '',
});

async function save() {
  return await form.submit((fields) => propertyStore.saveDescription(fields.description));
}

function copyDescription() {
  navigator.clipboard
    .writeText(form.fields.description)
    .then(() => {
      alertsStore.success('Description copied to clipboard');
    })
    .catch((error) => {
      console.error('Failed to copy description: ', error);
    });
}

function showHighlightsModal() {
  highlightsModalRef.value?.modalRef?.show();
}

async function generateDescription() {
  isGeneratingDescription.value = true;
  propertyStore
    .generateDescription()
    .then((description) => populateDescription(description))
    .catch((error) => {
      console.error('Failed to generate description: ', error);
      alertsStore.error("Couldn't write the description. Please try again.");
    })
    .finally(() => {
      isGeneratingDescription.value = false;
    });
}

function populateDescription(description: string) {
  form.fields.description = description;
}

watch(() => propertyStore.property?.description!, populateDescription, { immediate: true });

defineExpose({ save, generateDescription, isGeneratingDescription, form });
</script>

<style scoped lang="scss">
.field {
  position: relative;
}

.textarea {
  display: block;
  width: 100%;
}

.writing {
  position: absolute;
  // Inside the textarea's border, which stays put through the swap
  inset: 1px;
}

.writing-enter-active {
  transition: opacity 150ms var(--t-ease-out);
}

// The new text is already in the textarea underneath, so this fade is the reveal
.writing-leave-active {
  transition: opacity 250ms var(--t-ease-out);
}

.writing-enter-from,
.writing-leave-to {
  opacity: 0;
}

.controls {
  margin-top: var(--t-spacing-sm);
  display: flex;
  justify-content: flex-end;
  gap: var(--t-spacing-sm);
}

.highlights-bar {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: space-between;
  width: 100%;
  margin-bottom: var(--t-spacing-sm);
}
</style>
